import React, { useState } from "react";
import {
  Stack,
  Group,
  Text,
  ActionIcon,
  TextInput,
  Button,
  Box,
} from "@mantine/core";
import { IconChevronLeft, IconExternalLink } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import CustomRichTextEditor from "../../common/RichTextEditor";
import {
  hasFieldError,
  getFieldErrorMessage,
  getInputStyles,
  getLabelStyles,
  getLabelText,
} from "../utils/validation";

interface TeamContent {
  title?: string;
  body?: string;
}

interface WebsiteGeneratorTeamContentProps {
  teamContent?: TeamContent;
  onContentChange: (content: TeamContent) => void;
  onBack: () => void;
  missingFields?: string[];
  // Validation prop for field-level highlighting
  requiredValidation?: { [field: string]: boolean };
}

const WebsiteGeneratorTeamContent: React.FC<
  WebsiteGeneratorTeamContentProps
> = ({
  teamContent,
  onContentChange,
  onBack,
  missingFields = [],
  requiredValidation = {},
}) => {
  const navigate = useNavigate();
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  // Handle input changes
  const handleInputChange = (field: keyof TeamContent, value: string) => {
    // Validate character limit
    if (value.length > 100) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: `${field} cannot exceed 100 characters`,
      }));
      return;
    }

    // Clear validation error
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });

    // Update content immediately
    onContentChange({
      ...teamContent,
      [field]: value,
    });
  };

  // Handle textarea changes
  const handleTextareaChange = (field: keyof TeamContent, value: string) => {
    // Count words
    const wordCount = value
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length;

    // Validate word limit
    if (wordCount > 250) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: `${field} cannot exceed 250 words`,
      }));
      return;
    }

    // Clear validation error
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });

    // Update content immediately
    onContentChange({
      ...(teamContent || {}),
      [field]: value,
    });
  };

  // Count words for display
  const getWordCount = (text?: string) => {
    if (!text) return 0;
    return text
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
  };

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between" align="center">
        <ActionIcon variant="subtle" onClick={onBack}>
          <IconChevronLeft size={16} />
        </ActionIcon>
        <Text fw={600} size="lg">
          Team
        </Text>
      </Group>

      {/* Section Title */}
      <Box>
        <Text
          size="sm"
          fw={500}
          mb="xs"
          style={getLabelStyles(
            "title",
            requiredValidation,
            teamContent?.title,
          )}
        >
          {getLabelText(
            "Section Title",
            "title",
            requiredValidation,
            teamContent?.title,
          )}
        </Text>
        <TextInput
          placeholder="Your Team"
          value={teamContent?.title || ""}
          onChange={(event) =>
            handleInputChange("title", event.currentTarget.value)
          }
          error={getFieldErrorMessage(
            "title",
            requiredValidation,
            validationErrors,
            teamContent?.title,
          )}
          maxLength={100}
          styles={getInputStyles(
            "title",
            requiredValidation,
            missingFields,
            "about_us.header",
            teamContent?.title,
          )}
        />
      </Box>

      {/* Quote */}
      <Box>
        <Text
          size="sm"
          fw={500}
          mb="xs"
          style={getLabelStyles("body", requiredValidation, teamContent?.body)}
        >
          {getLabelText("Quote", "body", requiredValidation, teamContent?.body)}
        </Text>
        <CustomRichTextEditor
          label=""
          placeholder="Enter quote text..."
          value={teamContent?.body || ""}
          onChange={(htmlValue) => handleTextareaChange("body", htmlValue)}
          minHeight={180}
        />
        {hasFieldError("body", requiredValidation, teamContent?.body) && (
          <Text size="xs" c="red" mt="xs">
            Required
          </Text>
        )}
        {validationErrors.body && (
          <Text size="xs" c="red" mt="xs">
            {validationErrors.body}
          </Text>
        )}
        <Text size="xs" c="dimmed" mt="xs">
          {getWordCount(teamContent?.body)} words
        </Text>
      </Box>

      {/* Settings Link */}
      <Group gap="xs" mt="md">
        <Text size="sm" c="dimmed">
          Visit
        </Text>
        <Button
          variant="subtle"
          color="blue"
          size="sm"
          rightSection={<IconExternalLink size={14} />}
          onClick={() => navigate("/admin/settings/staff")}
        >
          Settings &gt; Staff
        </Button>
        <Text size="sm" c="dimmed">
          to edit staff members.
        </Text>
      </Group>
    </Stack>
  );
};

export default WebsiteGeneratorTeamContent;
